/**
 * Утилиты для работы с изображениями
 * 
 * Эти функции используются для локальных операций с изображениями
 * (загрузка по URL, конвертация в base64) и не связаны с основным API.
 * Используют нативный fetch, т.к. это клиентские операции без необходимости
 * проходить через axios-клиент.
 */

/**
 * Загружает изображение по URL и конвертирует в base64
 * @param imageUrl URL изображения
 * @returns Promise с base64 строкой
 */
export async function loadImageAsBase64(imageUrl: string): Promise<string> {
  try {
    const response = await fetch(imageUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to load image: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Failed to convert image to base64'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read image file'));
      };
      
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading image:', error);
    throw error;
  }
}

/** First image file from paste / drag-and-drop DataTransfer (or null). */
export function getImageFileFromDataTransfer(
  dataTransfer: DataTransfer | null | undefined,
): File | null {
  if (!dataTransfer) {
    return null
  }

  const candidates = [
    ...Array.from(dataTransfer.files ?? []),
    ...Array.from(dataTransfer.items ?? [])
      .filter((item) => item.kind === 'file')
      .map((item) => item.getAsFile())
      .filter((file): file is File => Boolean(file)),
  ]

  return candidates.find((file) => isLikelyImageFile(file)) ?? null
}

export function isLikelyImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) {
    return true
  }

  // Clipboard / some OS drops omit MIME — fall back to extension.
  return /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i.test(file.name)
}

